/**
 * Tenant Context — central source of truth for the active store_tenant
 * scope. Used by both the admin shell (when a store_manager signs in) and
 * the tenant storefront route (/store/:slug).
 *
 * When `tenantId` is set, cloudSync re-routes all `app_data` reads/writes
 * to `store_tenant_data` filtered by that tenant. When null, behaviour is
 * unchanged (main-site shared `app_data`).
 *
 * This is a *separate* layer from authentication — a tenant scope can be
 * set explicitly for read-only public viewing (storefront) regardless of
 * who is signed in.
 */
import { create } from "zustand";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  setCloudSyncTenantScope,
  loadFromCloud,
  type CloudTenantPermissions,
} from "@/data/cloudSync";
import { useStorefrontTheme, type ThemePreset } from "@/stores/storefrontThemeStore";
import { useLiteTabs, type LiteTabId } from "@/stores/liteTabsStore";

const ALL_THEMES: ThemePreset[] = ["classic", "modern", "minimal", "lite"];

/**
 * Store Managers are restricted to the Lite storefront by default. Super
 * Admin / Admin can enable additional themes per-tenant via
 * `store_tenant_permissions.permissions.enabled_themes`.
 */
function applyTenantThemeOverride(perms: CloudTenantPermissions | null): void {
  const raw = (perms as { permissions?: Record<string, unknown> } | null)?.permissions?.enabled_themes;
  const enabled = Array.isArray(raw) && raw.length > 0
    ? (raw as string[]).filter((t): t is ThemePreset => ALL_THEMES.includes(t as ThemePreset))
    : ["lite" as ThemePreset];
  const disabled = ALL_THEMES.filter(t => !enabled.includes(t));
  const current = useStorefrontTheme.getState();
  const adminDefault: ThemePreset = enabled.includes(current.adminDefault) ? current.adminDefault : (enabled[0] || "lite");
  useStorefrontTheme.getState().patch({
    adminDefault,
    disabledThemes: disabled,
    visitorToggleEnabled: enabled.length > 1,
  });
}

/**
 * Phase 1 — Super Admin / Admin can restrict which Lite nav tabs a Store
 * Manager's storefront shows via `store_tenant_permissions.nav_tabs`.
 * When a key is false, we force the matching Lite tab(s) disabled in the
 * tenant-scoped liteTabsStore.
 */
const NAV_TAB_MAP: Record<string, LiteTabId[]> = {
  available_now: ["home", "available"],
  favorites: ["favorites"],
  cart: ["cart"],
  tracking: ["track"],
  settings: ["settings"],
};

function applyTenantNavOverride(perms: CloudTenantPermissions | null): void {
  const nav = perms?.nav_tabs;
  if (!nav) return;
  const store = useLiteTabs.getState();
  for (const [key, tabIds] of Object.entries(NAV_TAB_MAP)) {
    const enabled = nav[key] !== false;
    if (!enabled) {
      for (const id of tabIds) store.patchTab(id, { enabled: false });
    }
  }
}


export interface TenantInfo {
  id: string;
  slug: string;
  store_title: string;
  hero_heading: string | null;
  description: string | null;
  logo_url: string | null;
  banner_url: string | null;
  status: string;
  business_categories?: string[] | null;
}

interface TenantState {
  /** Active tenant id; null = main site / no tenant scope. */
  tenantId: string | null;
  tenant: TenantInfo | null;
  permissions: CloudTenantPermissions | null;
  /** True while we are resolving the current user's tenant on sign-in. */
  resolving: boolean;
  setScope: (tenant: TenantInfo | null, permissions?: CloudTenantPermissions | null) => Promise<void>;
  clearScope: () => Promise<void>;
  _setResolving: (v: boolean) => void;
}

export const useTenantStore = create<TenantState>((set, get) => ({
  tenantId: null,
  tenant: null,
  permissions: null,
  resolving: false,

  setScope: async (tenant, permissions = null) => {
    set({ tenantId: tenant?.id ?? null, tenant, permissions });
    setCloudSyncTenantScope(
      tenant?.id ?? null,
      tenant ? {
        store_title: tenant.store_title,
        hero_heading: tenant.hero_heading,
        description: tenant.description,
        logo_url: tenant.logo_url,
        banner_url: tenant.banner_url,
      } : null,
    );
    if (tenant) {
      applyTenantThemeOverride(permissions);
      applyTenantNavOverride(permissions);
    }
    try { await loadFromCloud(); } catch {}
  },

  clearScope: async () => {
    set({ tenantId: null, tenant: null, permissions: null });
    setCloudSyncTenantScope(null);
    try { await loadFromCloud(); } catch {}
  },


  _setResolving: (v) => set({ resolving: v }),
}));

/**
 * Resolve the tenant for a given user id (must be a `store_manager`).
 * Returns null if the user owns no tenant or fetch fails.
 */
export async function resolveOwnedTenant(userId: string): Promise<{ tenant: TenantInfo; permissions: CloudTenantPermissions | null } | null> {
  try {
    const { data: tenant, error } = await supabase
      .from("store_tenants")
      .select("id, slug, store_title, hero_heading, description, logo_url, banner_url, status, business_categories")
      .eq("owner_user_id", userId)
      .maybeSingle();
    if (error || !tenant) return null;
    const { data: perms } = await supabase
      .from("store_tenant_permissions")
      .select("permissions, nav_tabs")
      .eq("tenant_id", tenant.id)
      .maybeSingle();
    return {
      tenant: tenant as TenantInfo,
      permissions: (perms as CloudTenantPermissions) ?? null,
    };
  } catch (e) {
    console.warn("resolveOwnedTenant failed", e);
    return null;
  }
}

/**
 * Mount this in the admin shell. It watches the signed-in user; when a
 * `store_manager` signs in, the tenant scope is set automatically; when
 * they sign out, it's cleared.
 */
export function useTenantAutoAttach() {
  const { user, profile } = useAuth();
  const role = profile?.role;
  const setScope = useTenantStore(s => s.setScope);
  const clearScope = useTenantStore(s => s.clearScope);
  const _setResolving = useTenantStore(s => s._setResolving);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user || role !== "store_manager") {
        await clearScope();
        return;
      }
      _setResolving(true);
      const resolved = await resolveOwnedTenant(user.id);
      if (cancelled) return;
      if (resolved) {
        await setScope(resolved.tenant, resolved.permissions);
      } else {
        await clearScope();
      }
      _setResolving(false);
    })();
    return () => { cancelled = true; };
  }, [user?.id, role, setScope, clearScope, _setResolving]);
}

/** Read-only accessor used by non-React code (e.g. edge-fn helpers). */
export function getCurrentTenantId(): string | null {
  return useTenantStore.getState().tenantId;
}
